import { actions } from "@mixitone/actions";
import { mixitoneImagesPublicUrl } from "@mixitone/util";
import tauri from "lib/tauri";

function normalizeContentTypeForUpload(contentType: string): string {
  return contentType.trim().split(";")[0].trim().toLowerCase();
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Presigns and uploads to R2 (`images.mixitone.com`). Returns the stable public HTTPS URL for the object.
 */
export async function uploadMixitoneImageObject(options: {
  accountId: string;
  relativePath: string;
  file: File;
}): Promise<string> {
  const rawType = options.file.type || "application/octet-stream";
  const contentType = normalizeContentTypeForUpload(rawType);
  if (!contentType.startsWith("image/") && contentType !== "application/pdf") {
    throw new Error("Unsupported file type");
  }

  const body = await options.file.arrayBuffer();
  const objectKey = `${options.accountId}/${options.relativePath}`;

  if (tauri.enabled) {
    await tauri.invoke("plugin:session|upload_mixitone_image", {
      accountId: options.accountId,
      relativePath: options.relativePath,
      contentType,
      dataBase64: arrayBufferToBase64(body),
      clubId: null,
    });
    return mixitoneImagesPublicUrl(objectKey);
  }

  const data = await actions.images.presignUpload({
    accountId: options.accountId,
    relativePath: options.relativePath,
    contentType,
  });

  // Use ArrayBuffer (not File) so the runtime does not add its own Content-Type from the Blob/File.
  const put = await fetch(data.putUrl, {
    method: "PUT",
    body,
    headers: { "Content-Type": data.contentType },
  });
  if (!put.ok) {
    const detail = (await put.text()).trim();
    const suffix = detail ? `: ${detail.slice(0, 800)}` : "";
    throw new Error(`Upload failed: ${put.status}${suffix}`);
  }
  return data.publicUrl;
}

export { mixitoneImagesPublicUrl };
